import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Copy, MonitorPlay, ImageUp, Trash2 } from "lucide-react";
import api, { errText } from "@/lib/api";

const FIELDS = [
  ["idle_note", "Prompt on the idle screen"],
  ["greeting", "Greeting when they scan"],
  ["welcome_line", "Line under the greeting"],
  ["repeat_greeting", "Greeting if already checked in"],
  ["repeat_line", "Line if already checked in"],
  ["invalid_line", "Line if the pass will not read"],
];

export const KioskPanel = ({ eventId, canEdit, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [background, setBackground] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const kioskUrl = `${window.location.origin}/kiosk/${eventId}`;

  useEffect(() => {
    if (!canEdit || !open || page) return;
    api
      .get(`/events/${eventId}/messages`)
      .then(({ data }) => {
        setPage(data.kiosk_page || {});
        setDefaults(data.kiosk_page_defaults || {});
        setBackground(data.banners?.kiosk_bg || "");
      })
      .catch((e) => toast.error(errText(e)));
  }, [canEdit, open, page, eventId]);

  const uploadBackground = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    const body = new FormData();
    body.append("file", file);
    try {
      const { data } = await api.post(`/events/${eventId}/banner`, body, {
        params: { kind: "kiosk_bg" },
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBackground(data.banners.kiosk_bg);
      toast.success("Kiosk background uploaded");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const removeBackground = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/events/${eventId}/banner`, { params: { kind: "kiosk_bg" } });
      setBackground(data.banners.kiosk_bg);
      toast.success("Kiosk background removed");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/events/${eventId}/messages`, { kiosk_page: page });
      setPage(data.kiosk_page);
      toast.success(data.kiosk_page?.enabled ? "Welcome kiosk is on" : "Welcome kiosk saved");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setSaving(false);
    }
  };

  const launch = () => window.open(kioskUrl, "_blank", "noopener");

  const copy = async () => {
    await navigator.clipboard.writeText(kioskUrl);
    toast.success("Kiosk link copied");
  };

  return (
    <div className={`card p-5 ${className}`} data-testid="kiosk-panel">
      <div className="flex items-center justify-between gap-3">
        <p className="label mb-0 flex items-center gap-2">
          <MonitorPlay size={14} className="text-[#00e599]" /> Welcome kiosk
        </p>
        {canEdit && (
          <button
            onClick={() => setOpen(!open)}
            className="btn-ghost text-xs"
            data-testid="kiosk-panel-toggle"
          >
            {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />} Settings
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-2">
        A full-screen greeter for a tablet or display at the entrance. Guests scan their own pass and
        are welcomed by name.
      </p>
      <div className="flex flex-wrap gap-3 mt-4">
        <button onClick={launch} className="btn-primary" data-testid="launch-kiosk-btn">
          <MonitorPlay size={15} /> Launch kiosk mode
        </button>
        <button onClick={copy} className="btn-secondary" data-testid="copy-kiosk-link-btn">
          <Copy size={15} /> Copy link
        </button>
      </div>

      {canEdit && open && page && (
        <div className="mt-5 pt-5 border-t border-[#252f3e]" data-testid="kiosk-settings">
          <label className="flex items-center gap-2 text-xs text-slate-300">
            <input
              type="checkbox"
              className="accent-[#00e599] w-4 h-4"
              data-testid="kiosk-enabled-toggle"
              checked={!!page.enabled}
              onChange={(e) => setPage({ ...page, enabled: e.target.checked })}
            />
            Switch the welcome kiosk on for this event
          </label>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            {FIELDS.map(([key, label]) => (
              <div key={key} className={key === "invalid_line" ? "sm:col-span-2" : ""}>
                <label className="label">{label}</label>
                <input
                  className="input"
                  data-testid={`kiosk-page-${key.replaceAll("_", "-")}-input`}
                  value={page[key] || ""}
                  onChange={(e) => setPage({ ...page, [key]: e.target.value })}
                />
              </div>
            ))}
            {[
              ["bg_color", "Background colour"],
              ["text_color", "Text colour"],
            ].map(([key, label]) => (
              <div key={key}>
                <label className="label">{label}</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    className="h-10 w-12 shrink-0 rounded-lg bg-transparent border border-[#252f3e] p-1 cursor-pointer"
                    data-testid={`kiosk-page-${key.replaceAll("_", "-")}-picker`}
                    value={/^#[0-9a-fA-F]{6}$/.test(page[key] || "") ? page[key] : "#ffffff"}
                    onChange={(e) => setPage({ ...page, [key]: e.target.value })}
                  />
                  <input
                    className="input font-mono2 text-xs"
                    placeholder="#ffffff"
                    data-testid={`kiosk-page-${key.replaceAll("_", "-")}-input`}
                    value={page[key] || ""}
                    onChange={(e) => setPage({ ...page, [key]: e.target.value })}
                  />
                </div>
              </div>
            ))}
          </div>
          <p className="font-mono2 text-[10px] text-slate-500 mt-3">
            Placeholders work here — {"{salutation_name}"} and {"{first_name}"} greet each guest by
            name.
          </p>

          <div className="mt-5 pt-5 border-t border-[#252f3e]" data-testid="kiosk-background-editor">
            <p className="label">Background image</p>
            <p className="text-[11px] text-slate-500 mt-1">
              Fills the whole kiosk screen. Without one, the event key visual is used as a faint wash.
            </p>
            {background && (
              <img
                src={background}
                alt="Kiosk background"
                className="mt-3 w-full h-28 object-cover rounded-xl border border-[#252f3e]"
                data-testid="kiosk-background-preview"
              />
            )}
            <div className="flex flex-wrap gap-3 mt-3">
              <label className="btn-secondary cursor-pointer" data-testid="kiosk-background-upload">
                <ImageUp size={15} /> {background ? "Replace image" : "Upload image"}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  disabled={busy}
                  onChange={uploadBackground}
                />
              </label>
              {background && (
                <button
                  onClick={removeBackground}
                  disabled={busy}
                  className="btn-ghost text-xs"
                  data-testid="kiosk-background-remove"
                >
                  <Trash2 size={13} /> Remove
                </button>
              )}
            </div>
            <label className="label mt-4 block">
              Dim the image — {page.bg_dim ?? 40}%
            </label>
            <input
              type="range"
              min="0"
              max="90"
              step="5"
              className="w-full accent-[#00e599]"
              data-testid="kiosk-bg-dim-slider"
              value={page.bg_dim ?? 40}
              onChange={(e) => setPage({ ...page, bg_dim: Number(e.target.value) })}
            />
            <p className="font-mono2 text-[10px] text-slate-500 mt-1">
              Tints the image with your background colour so names stay readable. Saved with "Save
              kiosk".
            </p>
          </div>
          <div className="flex flex-wrap gap-3 mt-4">
            <button
              onClick={save}
              disabled={saving}
              className="btn-primary"
              data-testid="kiosk-save-btn"
            >
              {saving ? "Saving…" : "Save kiosk"}
            </button>
            <button
              onClick={() => setPage({ ...defaults, enabled: page.enabled, bg_dim: page.bg_dim })}
              className="btn-secondary"
              data-testid="kiosk-reset-btn"
            >
              Restore default wording
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
